import { useDispatch, useSelector } from 'react-redux';
import { BatchDetailModel } from 'src/lib/hooks/batches/useBatchDetails';
import { ScheduleDetails } from 'src/lib/hooks/video-player/useScheduleDetails';
import { selectPlayerParams, updatePlayerFeatures } from 'src/lib/redux';
import { LocalStorageInstance } from 'src/services/localStroage/LocalStorageService';
import { isKhazanaPlaybackRequested } from 'src/utility';

type Props = { scheduleDetails: ScheduleDetails; batchDetails: BatchDetailModel };
type CommonArgs = Props & { isKhazanaVideo: boolean };

export type PlayerFeatures = {
  isKhazanaVideo: boolean;
  isCommentsFeatureEnabled: boolean;
  isAttachmentFeatureEnabled: boolean;
  isChatFeatureEnabled: boolean;
  isNotesFeatureEnabled: boolean;
  isPollFeatureEnabled: boolean;
  isTopicTimestampFeatureEnabled: boolean;
  isBatchDoubtFeatureEnabled: boolean;
  isTimelineFeatureEnabled: boolean;
  isNoteMakingFeatureEnabled: boolean;
  isRatingFeatureEnabled: boolean;
  isReportFeatureEnabled: boolean;
};

const useInitModuleFeatures = () => {
  const params = useSelector(selectPlayerParams);
  const dispatch = useDispatch();
  const { isUnderMaintenance, showProducts } = params;

  const checkIsCommentsFeatureEnabled = (args: CommonArgs) => {
    const { isKhazanaVideo, scheduleDetails } = args;
    if (isUnderMaintenance) {
      return false;
    }

    const isEnabledForSchedule = Boolean(
      scheduleDetails &&
        scheduleDetails?._id &&
        !scheduleDetails?.isLive &&
        !scheduleDetails.isCommentDisabled,
    );

    if (params.isCommentsEnabled) return true;

    return showProducts && (Boolean(isKhazanaVideo) || isEnabledForSchedule);
  };

  const checkIsAttachmentFeatureEnabled = (args: CommonArgs) => {
    const { isKhazanaVideo, scheduleDetails } = args;
    if (isKhazanaVideo === true) return false; // It's Khazana, no attachments
    return Boolean(scheduleDetails?.isVideoLecture); // Not Khazana, check schedule
  };

  const checkIsChatFeatureEnabled = (args: CommonArgs) => {
    const { scheduleDetails } = args;
    return (
      !isUnderMaintenance &&
      scheduleDetails?.isLive &&
      scheduleDetails?.isChatEnabled &&
      showProducts
    );
  };

  const checkIsNotesFeatureEnabled = (args: CommonArgs) => {
    const { isKhazanaVideo, scheduleDetails, batchDetails } = args;
    if (isKhazanaVideo) return false;

    const batchId = batchDetails._id;
    const notesEnabledBatchIds = LocalStorageInstance.getNotesEnabledBatchIds();
    let isNoteEnabled = false;
    if (notesEnabledBatchIds) {
      const parsedArray: string[] = JSON.parse(notesEnabledBatchIds);
      console.log('notes enabled', parsedArray, batchId);
      isNoteEnabled = parsedArray.includes(batchId) || parsedArray.includes('*');
      console.log('notes enabled 1', parsedArray, isNoteEnabled);
    } else {
      console.log('No data found in local storage for the given key.');
    }
    return !isUnderMaintenance && scheduleDetails?.isNoteEnabled && showProducts && isNoteEnabled;
  };

  const checkIsPollFeatureEnabled = (args: CommonArgs) => {
    // Polls are always enabled if not under maintenance
    return !isUnderMaintenance;
  };

  const checkIsTopicTimestampFeatureEnabled = (args: CommonArgs) => {
    const { isKhazanaVideo, scheduleDetails } = args;
    if (isKhazanaVideo) return false;
    return scheduleDetails?.isSubtopicTimestampEnabled;
  };

  const checkIsBatchDoubtFeatureEnabled = (args: CommonArgs) => {
    const { scheduleDetails } = args;
    return (
      scheduleDetails?.isBatchDoubtEnabled &&
      showProducts &&
      scheduleDetails?.isDoubtEnabled &&
      !isUnderMaintenance
    );
  };

  const checkIsTimelineFeatureEnabled = (args: CommonArgs) => {
    const { isKhazanaVideo } = args;
    if (isKhazanaVideo) return false;
    return !isUnderMaintenance && showProducts;
  };

  const checkIsNoteMakingFeatureEnabled = (args: CommonArgs) => {
    const { batchDetails } = args;
    const batchId = batchDetails._id;
    const notesEnabledBatchIds = LocalStorageInstance.getNotesEnabledBatchIds();
    let isNoteEnabled = false;
    if (notesEnabledBatchIds) {
      const parsedArray: string[] = JSON.parse(notesEnabledBatchIds);
      console.log('notes enabled', parsedArray, batchId);
      isNoteEnabled = parsedArray.includes(batchId) || parsedArray.includes('*');
      console.log('notes enabled 1', parsedArray, isNoteEnabled);
    } else {
      console.log('No data found in local storage for the given key.');
    }
    return isNoteEnabled;
  };

  const checkIsRatingFeatureEnabled = (args: CommonArgs) =>
    params.showHeader || params.isRatingEnabled;
  const checkIsReportFeatureEnabled = (args: CommonArgs) =>
    params.showHeader || params.isReportEnabled;

  const init = ({ scheduleDetails, batchDetails }: Props) => {
    console.log('Calling init with schedule');
    const isKhazanaVideo = isKhazanaPlaybackRequested(params);
    const commonArgs: CommonArgs = { scheduleDetails, batchDetails, isKhazanaVideo };

    const playerFeatures: PlayerFeatures = {
      isKhazanaVideo,
      isCommentsFeatureEnabled: checkIsCommentsFeatureEnabled(commonArgs),
      isAttachmentFeatureEnabled: checkIsAttachmentFeatureEnabled(commonArgs),
      isChatFeatureEnabled: checkIsChatFeatureEnabled(commonArgs),
      isNotesFeatureEnabled: checkIsNotesFeatureEnabled(commonArgs),
      isPollFeatureEnabled: checkIsPollFeatureEnabled(commonArgs),
      isTopicTimestampFeatureEnabled: checkIsTopicTimestampFeatureEnabled(commonArgs),
      isBatchDoubtFeatureEnabled: checkIsBatchDoubtFeatureEnabled(commonArgs),
      isTimelineFeatureEnabled: checkIsTimelineFeatureEnabled(commonArgs),
      isNoteMakingFeatureEnabled: checkIsNoteMakingFeatureEnabled(commonArgs),
      isRatingFeatureEnabled: checkIsRatingFeatureEnabled(commonArgs),
      isReportFeatureEnabled: checkIsReportFeatureEnabled(commonArgs),
    };
    dispatch(updatePlayerFeatures(playerFeatures));
  };

  const initWithoutSchedule = () => {
    console.log('Calling init without schedule');
    const isKhazanaVideo = isKhazanaPlaybackRequested(params);
    const scheduleDetails = {} as unknown as ScheduleDetails;
    const batchDetails = {} as unknown as BatchDetailModel;
    const commonArgs: CommonArgs = { scheduleDetails, batchDetails, isKhazanaVideo };

    const playerFeatures: PlayerFeatures = {
      isKhazanaVideo,
      isCommentsFeatureEnabled: checkIsCommentsFeatureEnabled(commonArgs),
      isAttachmentFeatureEnabled: checkIsAttachmentFeatureEnabled(commonArgs),
      isChatFeatureEnabled: checkIsChatFeatureEnabled(commonArgs),
      isNotesFeatureEnabled: checkIsNotesFeatureEnabled(commonArgs),
      isPollFeatureEnabled: checkIsPollFeatureEnabled(commonArgs),
      isTopicTimestampFeatureEnabled: checkIsTopicTimestampFeatureEnabled(commonArgs),
      isBatchDoubtFeatureEnabled: checkIsBatchDoubtFeatureEnabled(commonArgs),
      isTimelineFeatureEnabled: checkIsTimelineFeatureEnabled(commonArgs),
      isNoteMakingFeatureEnabled: checkIsNoteMakingFeatureEnabled(commonArgs),
      isRatingFeatureEnabled: checkIsRatingFeatureEnabled(commonArgs),
      isReportFeatureEnabled: checkIsReportFeatureEnabled(commonArgs),
    };
    dispatch(updatePlayerFeatures(playerFeatures));
  };

  return { init, initWithoutSchedule };
};

export default useInitModuleFeatures;
